import { useAsyncCallback } from "../hooks/useAsyncCallback";
import { useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { notificationPreferencesSchema } from "@io-spoke/contracts/notifications";
import { Button } from "@components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { Switch } from "@components/ui/switch";
import { toast } from "sonner";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@components/ui/empty";
import { Skeleton } from "@components/ui/skeleton";
import {
  getEntities,
  getNotificationPreferences,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  sendDailyDigest,
  getSummary,
  updateNotificationPreferences,
  type EntitySummary,
  type Notification,
  type SummaryPayload,
} from "../lib/auth";
import { PageHeader } from "@components/layout/PageHeader";
import { PageShell } from "@components/layout/PageShell";
import { getStoredUser } from "../lib/authStorage";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@components/ui/breadcrumb";
import { SidebarTrigger } from "@components/ui/sidebar";
import {
  DailyRateChart,
  DailyVolumeChart,
} from "../components/dashboard/DashboardSeriesCharts";
import type { OrgRoleView } from "../lib/roleViews";

type DashboardPageProps = {
  navigate: (route: string) => void;
  roleView?: OrgRoleView;
};

type NotificationFormInput = z.input<typeof notificationPreferencesSchema>;
type NotificationFormValues = z.output<typeof notificationPreferencesSchema>;

function resolveSummaryRange(range: string): "7" | "30" | "90" | "all" {
  if (range === "7d") {
    return "7";
  }
  if (range === "30d") {
    return "30";
  }
  if (range === "90d") {
    return "90";
  }
  return "all";
}

function formatPercent(value: number | null) {
  if (value === null) {
    return "n/a";
  }
  return `${(value * 100).toFixed(0)}%`;
}

function formatHours(value: number | null) {
  if (value === null) {
    return "n/a";
  }
  return `${value.toFixed(1)}h`;
}

export function DashboardPage({
  navigate,
  roleView = "manager",
}: DashboardPageProps) {
  const [dashboardEntities, setDashboardEntities] = useState<EntitySummary[]>(
    [],
  );
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [summary, setSummary] = useState<SummaryPayload | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [dashboardRange, setDashboardRange] = useState("30d");
  const [dashboardTeam, setDashboardTeam] = useState("all");
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationError, setNotificationError] = useState<string | null>(
    null,
  );
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationPrefsLoadError, setNotificationPrefsLoadError] = useState<
    string | null
  >(null);
  const notificationPrefsForm = useForm<
    NotificationFormInput,
    unknown,
    NotificationFormValues
  >({
    resolver: zodResolver(notificationPreferencesSchema),
    defaultValues: {
      teamId: "",
      roleId: "",
      inAppEnabled: true,
      digestEnabled: false,
      digestHourUtc: 13,
    },
  });

  useEffect(() => {
    let isActive = true;
    setIsDashboardLoading(true);
    setDashboardError(null);
    getEntities()
      .then((entities) => {
        if (!isActive) {
          return;
        }
        setDashboardEntities(entities);
      })
      .catch((err) => {
        if (!isActive) {
          return;
        }
        const message =
          err instanceof Error ? err.message : "Unable to load dashboard data.";
        setDashboardError(message);
      })
      .finally(() => {
        if (isActive) {
          setIsDashboardLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    setIsSummaryLoading(true);
    setSummaryError(null);

    getSummary({
      range: resolveSummaryRange(dashboardRange),
      team: dashboardTeam === "all" ? undefined : dashboardTeam,
    })
      .then((payload) => {
        if (!isActive) {
          return;
        }
        setSummary(payload);
      })
      .catch((err) => {
        if (!isActive) {
          return;
        }
        const message =
          err instanceof Error
            ? err.message
            : "Unable to load analytics summary.";
        setSummaryError(message);
      })
      .finally(() => {
        if (isActive) {
          setIsSummaryLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [dashboardRange, dashboardTeam]);

  useEffect(() => {
    let isActive = true;
    setNotificationPrefsLoadError(null);
    getNotificationPreferences()
      .then((prefs) => {
        if (!isActive) {
          return;
        }
        notificationPrefsForm.reset({
          teamId: prefs.teamId ?? "",
          roleId: prefs.roleId ?? "",
          inAppEnabled: prefs.inAppEnabled,
          digestEnabled: prefs.digestEnabled,
          digestHourUtc: prefs.digestHourUtc,
        });
      })
      .catch((err) => {
        if (!isActive) {
          return;
        }
        const message =
          err instanceof Error
            ? err.message
            : "Unable to load notification preferences.";
        setNotificationPrefsLoadError(message);
      });

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isActive = true;
    let timer: number | undefined;

    const loadNotifications = async () => {
      setNotificationLoading(true);
      setNotificationError(null);
      try {
        const data = await getNotifications({ status: "all", limit: 20 });
        if (isActive) {
          setNotifications(data);
        }
      } catch (err) {
        if (isActive) {
          const message =
            err instanceof Error
              ? err.message
              : "Unable to load notifications.";
          setNotificationError(message);
        }
      } finally {
        if (isActive) {
          setNotificationLoading(false);
        }
      }
    };

    loadNotifications();
    timer = window.setInterval(loadNotifications, 20000);

    return () => {
      isActive = false;
      if (timer) {
        window.clearInterval(timer);
      }
    };
  }, []);

  const user = getStoredUser() ?? {};
  const isAgentView = roleView === "agent";
  const isExecutiveView = roleView === "executive";
  const showAnalyticsOverview = !isAgentView;
  const showOperationalPanels = !isExecutiveView;

  const rangeDays =
    dashboardRange === "7d"
      ? 7
      : dashboardRange === "30d"
        ? 30
        : dashboardRange === "90d"
          ? 90
          : null;
  const now = Date.now();
  const cutoff = rangeDays ? now - rangeDays * 24 * 60 * 60 * 1000 : null;

  const dashboardTeams = useMemo(
    () =>
      Array.from(
        new Set(
          dashboardEntities
            .map((entity) => entity.assignment?.assigneeId)
            .filter((value): value is string => Boolean(value)),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [dashboardEntities],
  );

  const filteredDashboardEntities = useMemo(
    () =>
      dashboardEntities.filter((entity) => {
        if (cutoff && new Date(entity.updatedAt).getTime() < cutoff) {
          return false;
        }
        if (dashboardTeam !== "all") {
          return entity.assignment?.assigneeId === dashboardTeam;
        }
        return true;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cutoff is time-derived; recompute when the range selection changes
    [dashboardEntities, rangeDays, dashboardTeam],
  );

  const trendSeries = summary?.series ?? [];

  const handleExportAnalyticsCsv = () => {
    if (!summary || summary.series.length === 0) {
      toast.error("No analytics series available to export.");
      return;
    }

    const header = [
      "date",
      "total",
      "open",
      "blockers",
      "sla_eligible",
      "sla_compliant",
      "sla_compliance_rate",
    ];
    const rows = summary.series.map((point) => {
      const slaRate =
        point.slaEligible > 0
          ? (point.slaCompliant / point.slaEligible).toFixed(4)
          : "";
      return [
        point.date,
        String(point.total),
        String(point.open),
        String(point.blockers),
        String(point.slaEligible),
        String(point.slaCompliant),
        slaRate,
      ].join(",");
    });
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const teamKey =
      dashboardTeam === "all"
        ? "all-teams"
        : dashboardTeam.replace(/[^a-z0-9_-]+/gi, "-").toLowerCase();
    link.href = url;
    link.download = `dashboard-analytics-${dashboardRange}-${teamKey}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast.success("Analytics CSV exported.");
  };

  const breadcrumb = (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbPage>Dashboard</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  const {
    pending: saveNotificationPreferencesPending,
    callback: saveNotificationPreferences,
  } = useAsyncCallback(
    notificationPrefsForm.handleSubmit(async (values) => {
      try {
        const prefs = await updateNotificationPreferences(values);
        notificationPrefsForm.reset({
          teamId: prefs.teamId ?? "",
          roleId: prefs.roleId ?? "",
          inAppEnabled: prefs.inAppEnabled,
          digestEnabled: prefs.digestEnabled,
          digestHourUtc: prefs.digestHourUtc,
        });
        toast.success("Preferences saved.");
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Unable to save preferences.";
        toast.error(message);
      }
    }),
  );

  const {
    pending: markReadPending,
    activeArguments: readArguments,
    callback: markRead,
  } = useAsyncCallback(async (notificationId: string) => {
    try {
      await markNotificationRead(notificationId);
      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notificationId
            ? { ...item, readAt: new Date().toISOString() }
            : item,
        ),
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Unable to mark notifications read.",
      );
    }
  });
  const { pending: markAllReadPending, callback: markAllRead } =
    useAsyncCallback(async () => {
      try {
        await markAllNotificationsRead();
        setNotifications((prev) =>
          prev.map((item) => ({
            ...item,
            readAt: item.readAt ?? new Date().toISOString(),
          })),
        );
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Unable to mark notifications read.",
        );
      }
    });
  const { pending: sendDigestPending, callback: sendDigest } = useAsyncCallback(
    async () => {
      try {
        await sendDailyDigest();
        toast.success("Digest sent.");
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Unable to send digest.";
        toast.error(message);
      }
    },
  );

  return (
    <PageShell maxWidth="6xl">
      <PageHeader
        breadcrumb={breadcrumb}
        leading={<SidebarTrigger />}
        eyebrow="Customer impact overview"
        title={`Welcome${user?.name ? `, ${user.name}` : ""}.`}
        description="See what needs attention and move evidence-backed work forward."
        actions={
          <>
            <Button onClick={() => navigate("/obligations")}>
              Review obligations
            </Button>
          </>
        }
      />

      <section className="mt-8 space-y-6">
        <div className="space-y-6">
          <Card className="border-border bg-card/60">
            <CardHeader>
              <CardTitle className="text-lg">Needs attention</CardTitle>
              <CardDescription>
                Continue the work that requires a decision.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button className="min-h-11" onClick={() => navigate("/rescues")}>
                Open Rescue Room
              </Button>
              <Button
                className="min-h-11"
                variant="outline"
                onClick={() => navigate("/review")}
              >
                Review queue
              </Button>
            </CardContent>
          </Card>
          {showOperationalPanels ? (
            <Card className="border-border bg-card/60">
              <CardHeader>
                <CardTitle className="text-lg">Notifications</CardTitle>
                <CardDescription>
                  Stay on top of new assignments and SLA risk.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {notificationError ? (
                  <p
                    className="text-sm text-red-700 dark:text-red-400"
                    role="alert"
                  >
                    {notificationError}
                  </p>
                ) : null}
                {notificationLoading ? (
                  <div className="space-y-3">
                    {Array.from({ length: 3 }).map((_, index) => (
                      <div
                        key={`notification-skeleton-${index}`}
                        className="space-y-2 rounded-lg border border-border bg-muted/40 p-3"
                      >
                        <Skeleton className="h-4 w-2/3" />
                        <Skeleton className="h-3 w-1/3" />
                        <Skeleton className="h-7 w-20" />
                      </div>
                    ))}
                  </div>
                ) : notifications.length === 0 ? (
                  <Empty className="border-border/60 bg-muted/40">
                    <EmptyHeader>
                      <EmptyTitle>No notifications yet</EmptyTitle>
                      <EmptyDescription className="text-muted-foreground">
                        Activity updates and assignments will show up here.
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                ) : (
                  <div className="space-y-3">
                    {notifications.map((note) => (
                      <div
                        key={note.id}
                        className="space-y-2 rounded-lg border border-border bg-muted/40 p-3"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-foreground">
                              {note.title}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(note.createdAt).toLocaleString()}
                            </p>
                          </div>
                          {!note.readAt ? (
                            <Button
                              loading={
                                markReadPending &&
                                readArguments?.[0] === note.id
                              }
                              disabled={markReadPending}
                              loadingLabel="Marking read…"
                              variant="outline"
                              onClick={() => void markRead(note.id)}
                            >
                              Mark read
                            </Button>
                          ) : (
                            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70">
                              Read
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {note.body}
                        </p>
                        {note.entityId ? (
                          <Button
                            variant="outline"
                            onClick={() =>
                              navigate(`/entities/${note.entityId}`)
                            }
                          >
                            Open work item
                          </Button>
                        ) : null}
                      </div>
                    ))}
                    <Button
                      loading={markAllReadPending}
                      loadingLabel="Marking all read…"
                      variant="outline"
                      onClick={() => void markAllRead()}
                    >
                      Mark all read
                    </Button>
                  </div>
                )}
                <details
                  className="group border-t border-border pt-4"
                  open={Boolean(notificationPrefsLoadError)}
                >
                  <summary className="flex min-h-11 cursor-pointer list-none items-center rounded-md text-sm font-semibold text-foreground/90 outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                    Notification preferences
                  </summary>
                  <form
                    className="mt-3 space-y-3"
                    noValidate
                    onSubmit={saveNotificationPreferences}
                  >
                    {notificationPrefsLoadError ? (
                      <p
                        className="text-sm text-red-700 dark:text-red-400"
                        role="alert"
                      >
                        {notificationPrefsLoadError}
                      </p>
                    ) : null}
                    <div className="space-y-2">
                      <Label htmlFor="notify-team">Team</Label>
                      <Input
                        id="notify-team"
                        placeholder="support"
                        {...notificationPrefsForm.register("teamId")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="notify-role">Role</Label>
                      <Input
                        id="notify-role"
                        placeholder="oncall"
                        {...notificationPrefsForm.register("roleId")}
                      />
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <Label htmlFor="notify-inapp">In-app notifications</Label>
                      <Controller
                        name="inAppEnabled"
                        control={notificationPrefsForm.control}
                        render={({ field }) => (
                          <Switch
                            id="notify-inapp"
                            checked={Boolean(field.value)}
                            onCheckedChange={field.onChange}
                          />
                        )}
                      />
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <Label htmlFor="notify-digest">Daily digest email</Label>
                      <Controller
                        name="digestEnabled"
                        control={notificationPrefsForm.control}
                        render={({ field }) => (
                          <Switch
                            id="notify-digest"
                            checked={Boolean(field.value)}
                            onCheckedChange={field.onChange}
                          />
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="digest-hour">Digest hour (UTC)</Label>
                      <Input
                        id="digest-hour"
                        type="number"
                        min={0}
                        max={23}
                        {...notificationPrefsForm.register("digestHourUtc")}
                        aria-invalid={Boolean(
                          notificationPrefsForm.formState.errors.digestHourUtc,
                        )}
                        aria-describedby={
                          notificationPrefsForm.formState.errors.digestHourUtc
                            ? "digest-hour-error"
                            : undefined
                        }
                      />
                      {notificationPrefsForm.formState.errors.digestHourUtc ? (
                        <p
                          id="digest-hour-error"
                          className="text-xs text-red-700 dark:text-red-400"
                          role="alert"
                        >
                          {
                            notificationPrefsForm.formState.errors.digestHourUtc
                              .message
                          }
                        </p>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="submit"
                        loading={saveNotificationPreferencesPending}
                        loadingLabel="Saving preferences…"
                        disabled={notificationPrefsForm.formState.isSubmitting}
                      >
                        Save preferences
                      </Button>
                      <Button
                        loading={sendDigestPending}
                        loadingLabel="Sending digest…"
                        type="button"
                        variant="outline"
                        onClick={() => void sendDigest()}
                      >
                        Send digest now
                      </Button>
                    </div>
                  </form>
                </details>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </section>
      {showOperationalPanels ? (
        <section className="mt-10">
          <Card className="border-border bg-card/60">
            <CardHeader>
              <CardTitle className="text-lg">Active work items</CardTitle>
              <CardDescription>
                Prioritize items before they enter the queue.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isDashboardLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div
                      key={`dashboard-entity-skeleton-${index}`}
                      className="flex flex-col gap-2 rounded-lg border border-border bg-muted/40 p-4 md:flex-row md:items-center md:justify-between"
                    >
                      <div className="space-y-2">
                        <Skeleton className="h-3 w-32" />
                        <Skeleton className="h-5 w-48" />
                        <Skeleton className="h-3 w-40" />
                      </div>
                      <Skeleton className="h-9 w-32" />
                    </div>
                  ))}
                </div>
              ) : dashboardError ? (
                <p
                  className="text-sm text-red-700 dark:text-red-400"
                  role="alert"
                >
                  {dashboardError}
                </p>
              ) : filteredDashboardEntities.length === 0 ? (
                <Empty className="border-border/60 bg-muted/40">
                  <EmptyHeader>
                    <EmptyTitle>No work items match the filters</EmptyTitle>
                    <EmptyDescription className="text-muted-foreground">
                      Try widening the time range or selecting all teams.
                    </EmptyDescription>
                  </EmptyHeader>
                  <EmptyContent>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setDashboardRange("30d");
                        setDashboardTeam("all");
                      }}
                    >
                      Reset filters
                    </Button>
                  </EmptyContent>
                </Empty>
              ) : (
                filteredDashboardEntities.slice(0, 6).map((entity) => (
                  <div
                    key={entity.id}
                    className="flex flex-col gap-2 rounded-lg border border-border bg-muted/40 p-4 md:flex-row md:items-center md:justify-between"
                  >
                    <div className="space-y-1">
                      <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground/70">
                        {entity.type} - {entity.status}
                      </p>
                      <h3 className="text-lg font-semibold">{entity.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        Priority {entity.priority}
                        {entity.assignment?.assigneeId
                          ? ` - Owner ${entity.assignment.assigneeId}`
                          : ""}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/entities/${entity.id}`)}
                    >
                      Open work item
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </section>
      ) : null}
      {showAnalyticsOverview ? (
        <details
          className="mt-8 rounded-xl border border-border bg-card/60"
          open={Boolean(summaryError)}
        >
          <summary className="flex min-h-11 cursor-pointer items-center rounded-lg px-4 py-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            Summary filters and performance
          </summary>
          <div className="space-y-6 p-4 pt-0">
            <Card className="border-border bg-card/60">
              <CardHeader>
                <CardTitle className="text-lg">Summary filters</CardTitle>
                <CardDescription>
                  Scope summary metrics by time and team.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="dashboard-range">Time range</Label>
                  <Select
                    value={dashboardRange}
                    onValueChange={setDashboardRange}
                  >
                    <SelectTrigger id="dashboard-range">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7d">Last 7 days</SelectItem>
                      <SelectItem value="30d">Last 30 days</SelectItem>
                      <SelectItem value="90d">Last 90 days</SelectItem>
                      <SelectItem value="all">All time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dashboard-team">Team</Label>
                  <Select
                    value={dashboardTeam}
                    onValueChange={setDashboardTeam}
                  >
                    <SelectTrigger id="dashboard-team">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All teams</SelectItem>
                      {dashboardTeams.map((team) => (
                        <SelectItem key={team} value={team}>
                          {team}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
            <section className="mt-8 grid grid-cols-2 gap-6 md:grid-cols-5">
              {isSummaryLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <Card
                    key={`summary-skeleton-${index}`}
                    className="border-border bg-card/60"
                  >
                    <CardHeader className="space-y-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-3 w-32" />
                    </CardHeader>
                    <CardContent>
                      <Skeleton className="h-8 w-20" />
                    </CardContent>
                  </Card>
                ))
              ) : (
                <>
                  <Card className="border-border bg-card/60">
                    <CardHeader>
                      <CardTitle className="text-lg">Volume</CardTitle>
                      <CardDescription>Work items in scope.</CardDescription>
                    </CardHeader>
                    <CardContent className="text-3xl font-semibold">
                      {summary?.counts.total ?? 0}
                    </CardContent>
                  </Card>
                  <Card className="border-border bg-card/60">
                    <CardHeader>
                      <CardTitle className="text-lg">Trend</CardTitle>
                      <CardDescription>Vs. prior period.</CardDescription>
                    </CardHeader>
                    <CardContent className="text-3xl font-semibold">
                      {summary?.trend.percent === null ||
                      summary?.trend.percent === undefined
                        ? "-"
                        : `${summary.trend.percent.toFixed(0)}%`}
                    </CardContent>
                  </Card>
                  <Card className="border-border bg-card/60">
                    <CardHeader>
                      <CardTitle className="text-lg">Blockers</CardTitle>
                      <CardDescription>
                        High/critical open work.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="text-3xl font-semibold">
                      {summary?.counts.blockers ?? 0}
                    </CardContent>
                  </Card>
                  <Card className="border-border bg-card/60">
                    <CardHeader>
                      <CardTitle className="text-lg">SLA Compliance</CardTitle>
                      <CardDescription>
                        In-SLA ratio for scoped items.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="text-2xl font-semibold text-foreground/90">
                      {formatPercent(summary?.kpis.slaComplianceRate ?? null)}
                    </CardContent>
                  </Card>
                  <Card className="border-border bg-card/60">
                    <CardHeader>
                      <CardTitle className="text-lg">
                        Avg Approval Time
                      </CardTitle>
                      <CardDescription>
                        Time from creation to approval.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="text-2xl font-semibold text-foreground/90">
                      {formatHours(
                        summary?.kpis.averageTimeToApprovalHours ?? null,
                      )}
                    </CardContent>
                  </Card>
                </>
              )}
            </section>
            <section className="mt-8">
              <Card className="border-border bg-card/60">
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-lg">Workload Trend</CardTitle>
                    <CardDescription>
                      Daily totals, open load, blocker volume, and rate trend
                      views.
                    </CardDescription>
                  </div>
                  <Button variant="outline" onClick={handleExportAnalyticsCsv}>
                    Export CSV
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isSummaryLoading ? (
                    <div className="space-y-4">
                      <Skeleton className="h-64 w-full" />
                      <Skeleton className="h-64 w-full" />
                    </div>
                  ) : summaryError ? (
                    <p
                      className="text-sm text-red-700 dark:text-red-400"
                      role="alert"
                    >
                      {summaryError}
                    </p>
                  ) : trendSeries.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No trend data in the selected window.
                    </p>
                  ) : (
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground/80">
                          Daily volume
                        </p>
                        <DailyVolumeChart series={trendSeries} />
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground/80">
                          Daily rates
                        </p>
                        <DailyRateChart series={trendSeries} />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </section>
          </div>
        </details>
      ) : null}
    </PageShell>
  );
}
